import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Button, Grid, IconButton, Typography, Divider, Box, Dialog, DialogContent } from '@mui/material';
import _ from 'lodash';
import { Capacitor } from '@capacitor/core';
import { getAvatar } from '../../../utils/my-details';
import { getUserInitial } from '../../../utils/user';
import { THEME_COLOR_IMAGES, THEME_COLOR_PALETTES, getValidThemeColorOption } from '../../../constants/my-details';
import MyDetailsProfilePicture from '../../Advisor/MyDetails/MyDetailsProfile/MyDetailsProfilePicture/MyDetailsProfilePicture';
import { formatOpenUrl } from '../../../utils/url';
import { getVCardStringWithPhoto } from '../../../utils/vcard';
import { blobToDataURL, download } from '../../../utils/download';
import AdviserInfoPageCardBackground3 from '../AdviserInfoPageCardBackground/AdviserInfoPageCardBackground3';
import AdviserInfoCardAbstract from '../AdviserInfoCardAbstract/AdviserInfoCardAbstract';
import { getReferralLink } from '../../../utils/referral';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import NativeOrWeb from '../../../utils/native';
import { useRef } from 'react';
import QRCode from 'react-qr-code';
import './AdviserInfoPageCover.scss';

const ImageButtonStyle = {
  padding: 6
};

const ImageStyle = {
  width: 20,
  height: 20
};

const GridContainerStyle = {
  height: '100%'
};

const OnTopBackGroundStyle = {
  zIndex: 1,
  width: '100%',
  padding: 0
};

const AvatarGridStyle = {
  ...OnTopBackGroundStyle,
  padding: '25px 0 60px 0'
};

const TypoStyle = {
  whiteSpace: 'pre-wrap',
  maxWidth: '100%'
};

const NameTypoStyle = {
  ...TypoStyle,
  fontSize: '220%',
  letterSpacing: '-1px',
  maxWidth: '100%'
};

const JobTypoStyle = {
  ...TypoStyle,
  fontSize: '150%',
  paddingBottom: '6px',
  marginTop: '-3px',
  maxWidth: '100%'
};

const SaveMyContactButtonStyle = {
  borderRadius: 25,
  width: 100,
  background: '#fff',
  padding: '2px'
};

const FullWidthStyle = {
  width: '100%'
};

const AdviserInfoPageCover = props => {
  const { intl, userDetails, adviserInfo, isIntoView, onProfilePicClick, shakeOnWhatsapp } = props;

  const [wechatQrCodeDialogOpen, setWechatQrCodeDialogOpen] = useState(false);

  const {
    name,
    jobTitle,
    company,
    phoneNumber,
    whatsapp,
    wechatUrl,
    wechatId,
    workEmail,
    facebookLink,
    instagram,
    realLinkedinLink
  } = adviserInfo || {};
  const themeColor = getValidThemeColorOption(adviserInfo);
  const themeStyle = _.get(adviserInfo, 'themeStyle') || 'classic';
  const avatar = getAvatar(adviserInfo);
  const referralLink = getReferralLink({ targetAdviser: adviserInfo, userDetails: userDetails });

  const goPhoneNumber = () => {
    window.open(`tel:${phoneNumber}`, '_blank');
  };
  const whatsappButtonElem = useRef(null);
  useEffect(() => {
    // trigger squeeze on first render or if shakeOnWhatsapp is true
    if (shakeOnWhatsapp) {
      if (whatsappButtonElem.current) {
        whatsappButtonElem.current.classList.add('shake-once');
      }
      setTimeout(() => {
        if (whatsappButtonElem.current) {
          whatsappButtonElem.current.classList.remove('shake-once');
        }
      }, 2800);
    }
  }, [shakeOnWhatsapp]);

  const goWhatsapp = () => {
    window.open(
      `https://api.whatsapp.com/send?phone=${whatsapp.replace('+', '')}&text=${encodeURIComponent(
        intl.formatMessage({ id: 'adv-info-whatsapp-msg' }, { link: referralLink })
      )}`,
      '_blank'
    );
  };
  const goWechat = () => {
    if (Capacitor.isNativePlatform() || isMobileBrowser()) {
      let wechatMiniProgramUrl = `weixin://dl/business/?appid=wxd066ac768dfb3ef8&path=pages/qrCode/qrCode&query=userId%3D${
        adviserInfo?._id
      }&env_version=${process.env.STAGE === 'production' ? 'release' : 'trial'}`;
      window.open(wechatMiniProgramUrl, '_blank');
    } else {
      setWechatQrCodeDialogOpen(true);
    }
  };
  const goWorkEmail = () => {
    window.open(`mailto:${workEmail}`, '_blank');
  };
  const goFacebook = () => {
    window.open(formatOpenUrl(facebookLink), '_blank');
  };

  const goInstagram = () => {
    window.open(formatOpenUrl(`https://www.instagram.com/${instagram}`), '_blank');
  };

  const goLinkedin = () => {
    window.open(formatOpenUrl(realLinkedinLink), '_blank');
  };

  const saveMyContact = () => {
    getVCardStringWithPhoto(adviserInfo, string => download(string, `${name}.vcf`, 'text/x-vcard'));
  };
  const isMobileBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iphone|ipad|ipod|mobile/i.test(userAgent) && !Capacitor.isNativePlatform();
  };

  const onClickWechat = async () => {
    if (!wechatId) {
      return;
    }

    // Toast only when the copy actually landed — see the note on `copyToClipboard` for what
    // the web path can and cannot promise. The id stays readable on the card either way.
    let copied = false;
    try {
      copied = await NativeOrWeb.copyToClipboard(wechatId);
    } catch (error) {
      // leave copied false
    }

    if (copied) {
      toast.info(IconToast('info', intl.formatMessage({ id: 'wechat-msg-copied' })), { className: 'new-toast' });
    }
  };

  return (
    <>
      <Dialog open={wechatQrCodeDialogOpen} onClose={() => setWechatQrCodeDialogOpen(false)}>
        <DialogContent style={{ textAlign: 'center', padding: 24 }}>
          <QRCode size={120} style={{ height: 'auto' }} value={wechatUrl} viewBox={`0 0 64 64`} />
          <Typography style={{ marginTop: 12 }}>
            {' '}
            {intl.formatMessage({ id: 'open-wechat-and-scan-above-qr-code' })}
          </Typography>
          {wechatId && (
            <Typography onClick={onClickWechat} style={{ cursor: 'pointer' }}>
              {intl.formatMessage({ id: 'or-add-wechat-id' })} {wechatId}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <AdviserInfoCardAbstract
        className="adviser-info-page-cover"
        customHeader={
          <AdviserInfoPageCardBackground3
            style={{
              backgroundImage: `url("${THEME_COLOR_IMAGES[themeStyle][themeColor || 'blue']['mainTop']}")`
            }}
          />
        }
        customFooterStyle={{
          backgroundImage: `url("${THEME_COLOR_IMAGES[themeStyle][themeColor || 'blue']['mainFooter']}")`,
          height: 84
        }}
        contentStyle={{
          overflow: 'hidden'
        }}
        adviserInfo={adviserInfo}
        contentHeight="calc(100% - 160px)"
        isIntoView={isIntoView}
      >
        <Grid container wrap="nowrap" style={{ height: '100%' }} sx={{ flexDirection: 'column' }}>
          <Grid className="content-container" style={{ ...FullWidthStyle, flexGrow: 1, height: '100%' }}>
            <Grid
              container
              wrap="nowrap"
              spacing={2}
              style={GridContainerStyle}
              sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
            >
              <Grid style={AvatarGridStyle}>
                <MyDetailsProfilePicture
                  onProfilePicClick={onProfilePicClick}
                  initial={getUserInitial(adviserInfo)}
                  src={avatar}
                  width={150}
                  borderColor={THEME_COLOR_PALETTES[themeColor][1]}
                  extra={
                    <div
                      style={{
                        width: 160,
                        height: 160,
                        position: 'absolute',
                        top: 1,
                        left: 1,
                        borderRadius: '50%',
                        boxShadow: 'inset rgba(0, 0, 0, 0.6) 2px 3px 7px 1px'
                      }}
                    />
                  }
                />
              </Grid>

              <Grid
                style={{
                  ...OnTopBackGroundStyle,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  height: 'calc(100% - 256px)',
                  width: '100%'
                }}
              >
                <Grid
                  container
                  spacing={2}
                  style={{ maxWidth: '100%', margin: '-8px -4px' }}
                  sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Grid style={{ maxWidth: '100%' }}>
                    <Typography
                      align="center"
                      style={{
                        ...NameTypoStyle
                        // color: THEME_COLOR_PALETTES[themeColor][5],
                      }}
                    >
                      {name}
                    </Typography>

                    {jobTitle && (
                      <Typography
                        align="center"
                        style={{
                          ...JobTypoStyle,
                          color: THEME_COLOR_PALETTES[themeColor][5]
                        }}
                      >
                        {jobTitle}
                      </Typography>
                    )}

                    {company && (
                      <Typography
                        align="center"
                        style={{
                          ...TypoStyle,
                          color: THEME_COLOR_PALETTES[themeColor][5]
                        }}
                      >
                        {company}
                      </Typography>
                    )}
                  </Grid>
                  {/* {(phoneNumber || whatsapp || workEmail || facebookLink || instagram || realLinkedinLink) && (
                  <Grid style={OnTopBackGroundStyle}>
                    <Grid container  sx={{ alignItems: "center",  justifyContent: "center" }} spacing={1}>
                      {phoneNumber && (
                        <Grid>
                          <IconButton onClick={goPhoneNumber} style={ImageButtonStyle}>
                            <img src="img/phone.svg" alt="phone" style={ImageStyle} />
                          </IconButton>
                        </Grid>
                      )}
                      {workEmail && (
                        <Grid>
                          <IconButton onClick={goWorkEmail} style={ImageButtonStyle}>
                            <img src="img/email.svg" alt="email" style={ImageStyle} />
                          </IconButton>
                        </Grid>
                      )}
                      {facebookLink && (
                        <Grid>
                          <IconButton onClick={goFacebook} style={ImageButtonStyle}>
                            <img src="img/facebook.svg" alt="facebook" style={ImageStyle} />
                          </IconButton>
                        </Grid>
                      )}
                      {realLinkedinLink && (
                        <Grid>
                          <IconButton onClick={goLinkedin} style={ImageButtonStyle}>
                            <img src="img/linkedin.svg" alt="linkedin" style={ImageStyle} />
                          </IconButton>
                        </Grid>
                      )}
                      {instagram && (
                        <Grid>
                          <IconButton onClick={goInstagram} style={ImageButtonStyle}>
                            <img src="img/instagram.svg" alt="instagram" style={ImageStyle} />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )} */}

                  <Grid style={OnTopBackGroundStyle}>
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      {whatsapp && (
                        <Grid
                          size={wechatUrl ? 4 : 6}
                          className="large-icon-button-wrapper"
                          style={{ padding: 0, minWidth: 72, position: 'relative' }}
                        >
                          <Box
                            ref={whatsappButtonElem}
                            className={`large-icon-button whatsapp-button`}
                            onClick={goWhatsapp}
                          >
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 48 48"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect width="48" height="48" rx="24" fill="white" />
                              <path
                                d="M23.999 10.8008C30.4793 10.8008 35.7323 16.0538 35.7323 22.5341C35.7323 29.0144 30.4793 34.2674 23.999 34.2674C21.9254 34.271 19.8883 33.7222 18.0971 32.6776L12.2703 34.2674L13.8567 28.4383C12.8112 26.6466 12.262 24.6086 12.2656 22.5341C12.2656 16.0538 17.5187 10.8008 23.999 10.8008ZM20.0003 17.0194L19.7656 17.0288C19.6139 17.0393 19.4656 17.0791 19.3291 17.1462C19.2019 17.2183 19.0857 17.3084 18.9841 17.4137C18.8433 17.5463 18.7636 17.6613 18.6779 17.7727C18.2439 18.337 18.0102 19.0297 18.0138 19.7416C18.0161 20.3165 18.1663 20.8762 18.401 21.3995C18.8809 22.4578 19.6705 23.5784 20.7125 24.6168C20.9636 24.8667 21.21 25.1178 21.4751 25.3513C22.7698 26.4911 24.3126 27.3131 25.9807 27.7519L26.6472 27.854C26.8642 27.8657 27.0813 27.8493 27.2996 27.8387C27.6412 27.8207 27.9748 27.7282 28.2769 27.5677C28.4305 27.4883 28.5804 27.4022 28.7263 27.3096C28.7263 27.3096 28.776 27.2759 28.873 27.204C29.0314 27.0866 29.1288 27.0033 29.2602 26.866C29.3587 26.7644 29.4409 26.6462 29.5066 26.5117C29.5981 26.3204 29.6896 25.9555 29.7272 25.6516C29.7553 25.4193 29.7471 25.2926 29.7436 25.214C29.7389 25.0884 29.6345 24.9582 29.5207 24.9031L28.8378 24.5968C28.8378 24.5968 27.817 24.1521 27.1928 23.8682C27.1274 23.8397 27.0575 23.8234 26.9863 23.8201C26.906 23.8117 26.8248 23.8206 26.7483 23.8463C26.6718 23.8721 26.6017 23.9139 26.5428 23.9691C26.5369 23.9667 26.4583 24.0336 25.61 25.0615C25.5613 25.1269 25.4942 25.1763 25.4173 25.2035C25.3404 25.2307 25.2571 25.2343 25.1782 25.214C25.1017 25.1936 25.0268 25.1677 24.9541 25.1366C24.8086 25.0755 24.7581 25.0521 24.6584 25.0098C23.9847 24.7164 23.3612 24.3193 22.8104 23.833C22.6625 23.7039 22.5253 23.5631 22.3845 23.427C21.9229 22.9849 21.5206 22.4848 21.1877 21.9392L21.1184 21.8278C21.0695 21.7524 21.0293 21.6717 20.9988 21.5872C20.9542 21.4147 21.0703 21.2763 21.0703 21.2763C21.0703 21.2763 21.3554 20.9642 21.488 20.7952C21.6171 20.631 21.7262 20.4714 21.7966 20.3576C21.9351 20.1346 21.9785 19.9058 21.9057 19.7287C21.5772 18.9261 21.2377 18.1279 20.8873 17.3339C20.8181 17.1767 20.6127 17.064 20.4262 17.0417C20.3628 17.0339 20.2994 17.0277 20.2361 17.023C20.0785 17.0139 19.9206 17.0155 19.7632 17.0277L20.0003 17.0194Z"
                                fill={THEME_COLOR_PALETTES[themeColor][5]}
                              />
                            </svg>

                            <Typography>{intl.formatMessage({ id: 'WhatsApp' })}</Typography>
                          </Box>
                          <Divider
                            orientation="vertical"
                            flexItem
                            style={{ position: 'absolute', right: -4, height: '100%' }}
                          />
                        </Grid>
                      )}
                      {wechatUrl && (
                        <Grid
                          size={whatsapp ? 4 : 6}
                          className="large-icon-button-wrapper"
                          style={{ padding: 0, minWidth: 72, position: 'relative' }}
                        >
                          <Box className="large-icon-button" onClick={goWechat}>
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 48 48"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect width="48" height="48" rx="24" fill="white" />
                              <path
                                d="M29.6478 18.3394C30.2198 18.3394 30.7771 18.3834 31.3198 18.4568C30.3225 14.1008 25.7465 10.8008 20.2465 10.8008C14.0131 10.8008 8.95312 15.0248 8.95312 20.2314C8.95312 23.2381 10.6398 25.8928 13.2651 27.6234L11.7838 30.6008L15.8318 28.8554C16.6971 29.1634 17.6065 29.4128 18.5745 29.5448C18.4425 28.9728 18.3691 28.3861 18.3691 27.7701C18.3545 22.5781 23.4145 18.3394 29.6478 18.3394ZM24.0011 15.0394C24.186 15.0394 24.3691 15.0759 24.5399 15.1466C24.7108 15.2174 24.866 15.3211 24.9967 15.4518C25.1275 15.5826 25.2312 15.7378 25.3019 15.9086C25.3727 16.0795 25.4091 16.2625 25.4091 16.4474C25.4091 16.6323 25.3727 16.8154 25.3019 16.9863C25.2312 17.1571 25.1275 17.3123 24.9967 17.4431C24.866 17.5738 24.7108 17.6775 24.5399 17.7483C24.3691 17.819 24.186 17.8554 24.0011 17.8554C23.6277 17.8554 23.2696 17.7071 23.0055 17.4431C22.7415 17.179 22.5931 16.8209 22.5931 16.4474C22.5931 16.074 22.7415 15.7159 23.0055 15.4518C23.2696 15.1878 23.6277 15.0394 24.0011 15.0394ZM16.4771 17.8701C16.1037 17.8701 15.7456 17.7218 15.4815 17.4577C15.2175 17.1937 15.0691 16.8355 15.0691 16.4621C15.0691 16.0887 15.2175 15.7306 15.4815 15.4665C15.7456 15.2025 16.1037 15.0541 16.4771 15.0541C16.8506 15.0541 17.2087 15.2025 17.4727 15.4665C17.7368 15.7306 17.8851 16.0887 17.8851 16.4621C17.8851 16.8355 17.7368 17.1937 17.4727 17.4577C17.2087 17.7218 16.8506 17.8701 16.4771 17.8701Z"
                                fill={THEME_COLOR_PALETTES[themeColor][5]}
                              />
                              <path
                                d="M39.0507 27.7691C39.0507 23.6038 34.8414 20.2305 29.6494 20.2305C24.4574 20.2305 20.248 23.6038 20.248 27.7691C20.248 31.9345 24.4574 35.3078 29.6494 35.3078C30.5 35.3078 31.3214 35.1905 32.0987 35.0145L37.1734 37.1998L35.4134 33.6798C37.6134 32.3011 39.0507 30.1891 39.0507 27.7691ZM26.8187 27.2998C26.5402 27.2998 26.268 27.2172 26.0365 27.0625C25.8049 26.9078 25.6245 26.6879 25.5179 26.4306C25.4113 26.1733 25.3834 25.8902 25.4378 25.6171C25.4921 25.344 25.6262 25.0931 25.8231 24.8962C26.02 24.6993 26.2709 24.5652 26.544 24.5109C26.8172 24.4565 27.1003 24.4844 27.3575 24.591C27.6148 24.6975 27.8347 24.878 27.9894 25.1096C28.1441 25.3411 28.2267 25.6133 28.2267 25.8918C28.2414 26.6691 27.596 27.2998 26.8187 27.2998ZM32.4654 27.2998C32.092 27.2998 31.7338 27.1515 31.4698 26.8874C31.2057 26.6234 31.0574 26.2652 31.0574 25.8918C31.0574 25.5184 31.2057 25.1602 31.4698 24.8962C31.7338 24.6321 32.092 24.4838 32.4654 24.4838C32.8388 24.4838 33.1969 24.6321 33.461 24.8962C33.725 25.1602 33.8734 25.5184 33.8734 25.8918C33.8734 26.2652 33.725 26.6234 33.461 26.8874C33.1969 27.1515 32.8388 27.2998 32.4654 27.2998Z"
                                fill={THEME_COLOR_PALETTES[themeColor][5]}
                              />
                            </svg>

                            <Typography>{intl.formatMessage({ id: 'wechat' })}</Typography>
                          </Box>
                          <Divider
                            orientation="vertical"
                            flexItem
                            style={{ position: 'absolute', right: 4, height: '100%' }}
                          />
                        </Grid>
                      )}
                      <Grid
                        size={whatsapp ? (wechatUrl ? 4 : 6) : wechatUrl ? 6 : 12}
                        className="large-icon-button-wrapper"
                        style={{ padding: 0, minWidth: 72 }}
                      >
                        <Box className="large-icon-button" onClick={saveMyContact}>
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect width="48" height="48" rx="24" fill="white" />
                            <path
                              d="M34.4874 10.8008H15.7874C15.1262 10.8008 14.492 11.0635 14.0244 11.5311C13.5568 11.9987 13.2941 12.6328 13.2941 13.2941V17.0341H10.8008V19.5274H13.2941V22.0208H10.8008V24.5141H13.2941V27.0074H10.8008V29.5008H13.2941V33.2408C13.2941 33.9021 13.5568 34.5362 14.0244 35.0038C14.492 35.4714 15.1262 35.7341 15.7874 35.7341H34.4874C34.8181 35.7341 35.1352 35.6028 35.369 35.369C35.6028 35.1352 35.7341 34.8181 35.7341 34.4874V12.0474C35.7341 11.7168 35.6028 11.3997 35.369 11.1659C35.1352 10.9321 34.8181 10.8008 34.4874 10.8008ZM24.5141 14.5395C26.5686 14.5395 28.2541 16.2238 28.2541 18.2795C28.2512 19.2706 27.8562 20.2203 27.1555 20.9213C26.4548 21.6222 25.5052 22.0175 24.5141 22.0208C22.4609 22.0208 20.7741 20.334 20.7741 18.2795C20.7741 16.2238 22.4609 14.5395 24.5141 14.5395ZM31.9941 30.7474H17.0341V29.8124C17.0341 27.0461 20.4063 24.2024 24.5141 24.2024C28.6219 24.2024 31.9941 27.0461 31.9941 29.8124V30.7474Z"
                              fill={THEME_COLOR_PALETTES[themeColor][5]}
                            />
                          </svg>

                          <Typography>{intl.formatMessage({ id: 'adv-info-save-my-contact' })}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AdviserInfoCardAbstract>
    </>
  );
};

export default injectIntl(AdviserInfoPageCover);
